import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Divider,
  Modal,
  Progress,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DollarOutlined,
  EyeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import apiClient from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";
import ReturnPurchasePaymentModal from "./ReturnPurchasePaymentModal";

interface ReturnPurchaseDetailModalProps {
  order: any | null;
  visible: boolean;
  onClose: () => void;
  produits: any[];
  taxes: any[];
  fournisseurs: any[];
  refreshOrderDetails: () => void | Promise<void>;
  refreshRetourAchats: () => void | Promise<void>;
}

interface ReturnPurchasePayment {
  id?: number;
  payment_id?: number;
  order_payment_id?: number;
  payment_number?: string;
  date?: string;
  amount?: number | string;
  payment_amount?: number | string;
  payment_mode_name?: string;
  notes?: string;
  remarks?: string;
}

const { Text } = Typography;

const formatMoney = (value: unknown): string => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    : "0";
};

const formatQuantity = (value: unknown): string => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      })
    : "0";
};

const normalizeStatus = (value: unknown): string =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const isCancelledReturn = (status: unknown): boolean => {
  const normalized = normalizeStatus(status);
  return normalized.includes("annul") || normalized.includes("cancel");
};

const getPaymentStatusTag = (status: unknown) => {
  const normalized = normalizeStatus(status);
  const color = normalized.includes("part")
    ? "orange"
    : normalized.includes("non") || normalized.includes("unpaid")
      ? "red"
      : normalized.includes("pay") || normalized.includes("paye")
        ? "green"
        : "default";
  return <Tag color={color}>{String(status || "Non payé")}</Tag>;
};

const getOrderStatusTag = (status: unknown) => {
  const normalized = normalizeStatus(status);
  const color = isCancelledReturn(status)
    ? "red"
    : normalized.includes("complet") || normalized.includes("complete")
      ? "green"
      : "blue";
  return <Tag color={color}>{String(status || "Non spécifié")}</Tag>;
};

const ReturnPurchaseDetailModal: React.FC<
  ReturnPurchaseDetailModalProps
> = ({
  order,
  visible,
  onClose,
  produits,
  taxes,
  fournisseurs,
  refreshOrderDetails,
  refreshRetourAchats,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [payments, setPayments] = useState<ReturnPurchasePayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] =
    useState<ReturnPurchasePayment | null>(null);
  const { hasPermission } = useAuth();
  const canViewPayments = hasPermission(
    "Gestion Commerciale.Approvisionnement.Achats.RetourAchat.view"
  );
  const canCreateIncomingPayment = hasPermission(
    "Gestion Commerciale.Ventes.PaiementsEntrants.create"
  );

  const fetchOrderPayments = useCallback(async () => {
    if (!visible || !order?.id || !canViewPayments) {
      setPayments([]);
      setLoadingPayments(false);
      return;
    }

    setLoadingPayments(true);
    setPaymentError(null);
    try {
      const response = await apiClient.get(`/api/orders/${order.id}/payments`, {
        params: {
          company_id: order.company_id,
          warehouse_id: order.warehouse_id,
        },
      });
      const rows = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.payments)
          ? response.data.payments
          : [];
      setPayments(rows);
    } catch (error: any) {
      console.error(
        "Erreur lors du chargement des paiements du retour d'achat:",
        error
      );
      setPayments([]);
      setPaymentError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Impossible de charger l'historique des paiements."
      );
    } finally {
      setLoadingPayments(false);
    }
  }, [
    canViewPayments,
    order?.company_id,
    order?.id,
    order?.warehouse_id,
    visible,
  ]);

  useEffect(() => {
    if (visible) {
      setActiveTab("details");
      void fetchOrderPayments();
    } else {
      setPayments([]);
      setPaymentError(null);
      setSelectedPayment(null);
      setPaymentModalVisible(false);
    }
  }, [fetchOrderPayments, visible]);

  const supplierName = useMemo(() => {
    if (order?.entity_name || order?.user_name) {
      return order.entity_name || order.user_name;
    }
    const supplier = fournisseurs.find(
      (entry) => Number(entry?.id) === Number(order?.user_id)
    );
    return supplier?.name || supplier?.company_name || "Fournisseur inconnu";
  }, [fournisseurs, order]);

  if (!order) return null;

  const total = Number(order.total ?? 0);
  const paid = Number(order.paid_amount ?? 0);
  const remaining = Math.max(0, total - paid);
  const percentage =
    total > 0 ? Math.min(100, Math.max(0, Math.round((paid / total) * 100))) : 0;
  const subtotal = Number(
    order.subtotal ??
      (order.items || []).reduce(
        (sum: number, item: any) => sum + Number(item.subtotal ?? 0),
        0
      )
  );
  const discount = Number(order.discount_amount ?? order.discount ?? 0);
  const tax = Number(order.tax_amount ?? 0);
  const shipping = Number(order.shipping_cost ?? order.shipping ?? 0);
  const canAddPayment =
    remaining > 0 &&
    !isCancelledReturn(order.order_status) &&
    canCreateIncomingPayment;
  const totalPayments = payments.reduce(
    (sum, payment) =>
      sum + Number(payment.payment_amount ?? payment.amount ?? 0),
    0
  );

  const productColumns: any[] = [
    {
      title: "Article",
      dataIndex: "product_name",
      key: "product_name",
      render: (name: string, record: any) => {
        if (name) return name;
        const product = produits.find(
          (entry) => Number(entry?.id) === Number(record?.product_id)
        );
        return product?.name || `Produit #${record?.product_id || "-"}`;
      },
    },
    {
      title: "Quantité",
      dataIndex: "quantity",
      key: "quantity",
      align: "right" as const,
      render: formatQuantity,
    },
    {
      title: "Prix unitaire",
      dataIndex: "unit_price",
      key: "unit_price",
      align: "right" as const,
      render: (value: unknown) => `${formatMoney(value)} XOF`,
    },
    {
      title: "Remise",
      dataIndex: "discount_rate",
      key: "discount_rate",
      align: "right" as const,
      render: (value: unknown) => `${formatMoney(value)} %`,
    },
    {
      title: "Taxe",
      dataIndex: "tax_rate",
      key: "tax_rate",
      align: "right" as const,
      render: (value: unknown, record: any) => {
        if (value !== undefined && value !== null) return `${formatMoney(value)} %`;
        const taxRecord = taxes.find(
          (entry) => Number(entry?.id) === Number(record?.tax_id)
        );
        return taxRecord ? `${taxRecord.name} (${taxRecord.rate} %)` : "-";
      },
    },
    {
      title: "Sous-total",
      dataIndex: "subtotal",
      key: "subtotal",
      align: "right" as const,
      render: (value: unknown) => <strong>{formatMoney(value)} XOF</strong>,
    },
  ];

  const paymentColumns: any[] = [
    {
      title: "N° paiement",
      dataIndex: "payment_number",
      key: "payment_number",
      render: (value: string, record: ReturnPurchasePayment) =>
        value || `#${record.payment_id ?? record.id ?? "-"}`,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (value: string) =>
        value && dayjs(value).isValid() ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Mode",
      dataIndex: "payment_mode_name",
      key: "payment_mode_name",
      render: (value: string) => value || "-",
    },
    {
      title: "Montant affecté",
      key: "payment_amount",
      align: "right" as const,
      render: (_: unknown, record: ReturnPurchasePayment) =>
        `${formatMoney(record.payment_amount ?? record.amount)} XOF`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      render: (_: unknown, record: ReturnPurchasePayment) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => setSelectedPayment(record)}
        >
          Détails
        </Button>
      ),
    },
  ];

  const handlePaymentAdded = async () => {
    setPaymentModalVisible(false);
    await refreshOrderDetails();
    await refreshRetourAchats();
    await fetchOrderPayments();
  };

  const detailContent = (
    <>
      <Card title="Informations du retour d'achat" size="small">
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="N° de retour">
            {order.invoice_number || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            {order.order_date && dayjs(order.order_date).isValid()
              ? dayjs(order.order_date).format("DD/MM/YYYY")
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Fournisseur">
            {supplierName}
          </Descriptions.Item>
          <Descriptions.Item label="Établissement">
            {order.warehouse_name || "Non spécifié"}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            {getOrderStatusTag(order.order_status)}
          </Descriptions.Item>
          <Descriptions.Item label="Statut paiement">
            {getPaymentStatusTag(order.payment_status)}
          </Descriptions.Item>
          {order.original_order_id ? (
            <Descriptions.Item label="Achat d'origine" span={2}>
              #{order.original_order_id}
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      </Card>

      <Card title="Résumé financier" size="small" style={{ marginTop: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Sous-total">
            {formatMoney(subtotal)} XOF
          </Descriptions.Item>
          <Descriptions.Item label="Remise">
            {formatMoney(discount)} XOF
          </Descriptions.Item>
          <Descriptions.Item label="Taxes">
            {formatMoney(tax)} XOF
          </Descriptions.Item>
          <Descriptions.Item label="Expédition">
            {formatMoney(shipping)} XOF
          </Descriptions.Item>
          <Descriptions.Item label="Montant total">
            <Text strong style={{ color: "#1677ff" }}>
              {formatMoney(total)} XOF
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Déjà encaissé">
            <Text strong type="success">
              {formatMoney(paid)} XOF
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Reste à encaisser" span={2}>
            <Text strong type={remaining > 0 ? "danger" : "success"}>
              {formatMoney(remaining)} XOF
            </Text>
          </Descriptions.Item>
        </Descriptions>
        <Progress
          percent={percentage}
          status={remaining <= 0 ? "success" : "active"}
          style={{ marginTop: 12, marginBottom: 0 }}
        />
      </Card>

      <Divider orientation="left">Articles retournés</Divider>
      <Table
        dataSource={Array.isArray(order.items) ? order.items : []}
        columns={productColumns}
        rowKey={(record, index) =>
          String(record.id ?? record.original_order_item_id ?? index)
        }
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 760 }}
      />

      <Divider orientation="left">Notes et conditions</Divider>
      <Card size="small">
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label="Notes">
            {order.notes || "Aucune note"}
          </Descriptions.Item>
          <Descriptions.Item label="Termes et conditions">
            {order.terms_condition || "Aucune condition spécifiée"}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </>
  );

  const paymentsContent = (
    <>
      {paymentError ? (
        <Alert
          type="error"
          showIcon
          message="Historique indisponible"
          description={paymentError}
          action={
            <Button size="small" onClick={() => void fetchOrderPayments()}>
              Réessayer
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <Text strong>Total encaissé : {formatMoney(totalPayments)} XOF</Text>
        {canAddPayment ? (
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => setPaymentModalVisible(true)}
          >
            Ajouter un paiement
          </Button>
        ) : null}
      </div>
      <Table
        dataSource={payments}
        columns={paymentColumns}
        rowKey={(record) =>
          String(
            record.payment_id ??
              record.order_payment_id ??
              record.id ??
              record.payment_number
          )
        }
        loading={loadingPayments}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 680 }}
        locale={{ emptyText: "Aucun paiement enregistré pour ce retour d'achat." }}
      />
    </>
  );

  return (
    <>
      <Modal
        title={
          <Space>
            <InfoCircleOutlined style={{ color: "#1677ff" }} />
            <span>Détails du retour d'achat - {order.invoice_number}</span>
          </Space>
        }
        open={visible}
        onCancel={onClose}
        width={1000}
        destroyOnClose
        styles={{ body: { maxHeight: "calc(100vh - 170px)", overflowY: "auto" } }}
        footer={[
          canAddPayment ? (
            <Button
              key="payment"
              type="primary"
              icon={<DollarOutlined />}
              onClick={() => setPaymentModalVisible(true)}
            >
              Ajouter un paiement
            </Button>
          ) : null,
          <Button key="close" onClick={onClose}>
            Fermer
          </Button>,
        ]}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "details",
              label: "Informations générales",
              children: detailContent,
            },
            {
              key: "payments",
              label: `Paiements (${payments.length})`,
              disabled: !canViewPayments,
              children: paymentsContent,
            },
          ]}
        />
      </Modal>

      <ReturnPurchasePaymentModal
        visible={paymentModalVisible}
        onClose={() => setPaymentModalVisible(false)}
        order={order}
        onPaymentAdded={handlePaymentAdded}
      />

      <Modal
        title={`Détails du paiement${
          selectedPayment?.payment_number
            ? ` - ${selectedPayment.payment_number}`
            : ""
        }`}
        open={Boolean(selectedPayment)}
        onCancel={() => setSelectedPayment(null)}
        width={560}
        footer={[
          <Button key="close" onClick={() => setSelectedPayment(null)}>
            Fermer
          </Button>,
        ]}
      >
        {selectedPayment ? (
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="N° paiement">
              {selectedPayment.payment_number || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {selectedPayment.date && dayjs(selectedPayment.date).isValid()
                ? dayjs(selectedPayment.date).format("DD/MM/YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Mode">
              {selectedPayment.payment_mode_name || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Montant affecté">
              {formatMoney(
                selectedPayment.payment_amount ?? selectedPayment.amount
              )}{" "}
              XOF
            </Descriptions.Item>
            <Descriptions.Item label="Montant du paiement" span={2}>
              {formatMoney(selectedPayment.amount)} XOF
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {selectedPayment.notes ||
                selectedPayment.remarks ||
                "Aucune note"}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>
    </>
  );
};

export default ReturnPurchaseDetailModal;
